import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { PRRunHistoryModal } from "@/components/PRRunHistoryModal";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@/components/ui/table";
import { useAuthStatus } from "@/lib/auth";
import { formatRelativeTime } from "@/lib/time";

type Project = {
  id: number;
  owner: string;
  name: string;
  created_at: string;
  review_enabled: boolean;
  babysit_enabled: boolean;
};

type PullRequest = {
  ID: number;
  RepoID: number;
  Number: number;
  Title: string;
  State: string;
  AuthorLogin: string;
  HeadSHA: string;
  BaseBranch: string;
  HeadBranch: string;
  UpdatedAt: string;
  CreatedAt: string;
  MergedAt: string | null;
  ClosedAt: string | null;
};

type TaggedPR = PullRequest & { repo: string };

type BadgeVariant = "default" | "secondary" | "outline" | "destructive";

type RunState = "idle" | "starting" | "started" | "error";
type WorkflowName = "pr-review" | "babysit";

function prStateVariant(state: string): BadgeVariant {
  switch (state.toLowerCase()) {
    case "open":
      return "default";
    case "merged":
      return "secondary";
    default:
      return "outline";
  }
}

type ActionButtonProps = {
  prRef: string;
  isMine: boolean;
  state: RunState;
  onStart: (prRef: string, workflowName: WorkflowName) => void;
};

function ActionButton({ prRef, isMine, state, onStart }: ActionButtonProps) {
  const workflowName: WorkflowName = isMine ? "babysit" : "pr-review";
  const label = isMine ? "Babysit" : "Review";

  if (state === "starting") {
    return (
      <Button variant="outline" size="sm" disabled>
        <Loader2 className="animate-spin" />
        {label}
      </Button>
    );
  }
  if (state === "started") {
    return (
      <Button variant="outline" size="sm" disabled>
        Started ✓
      </Button>
    );
  }
  if (state === "error") {
    return (
      <Button
        variant="outline"
        size="sm"
        className="text-destructive border-destructive hover:text-destructive"
        title="Failed to start run — click to retry"
        onClick={() => onStart(prRef, workflowName)}
      >
        Retry
      </Button>
    );
  }
  return (
    <Button variant="outline" size="sm" onClick={() => onStart(prRef, workflowName)}>
      {label}
    </Button>
  );
}

export function DashboardPRList() {
  const { status } = useAuthStatus();
  const login = status?.user ?? "";

  const [pulls, setPulls] = useState<TaggedPR[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runStates, setRunStates] = useState<Record<string, RunState>>({});
  const [selectedPR, setSelectedPR] = useState<TaggedPR | null>(null);

  const startRun = useCallback(async (prRef: string, workflowName: WorkflowName) => {
    setRunStates((prev) => ({ ...prev, [prRef]: "starting" }));
    try {
      const res = await fetch("/api/runs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ workflowName, prRef }),
      });
      if (!res.ok) throw new Error(await res.text());
      setRunStates((prev) => ({ ...prev, [prRef]: "started" }));
    } catch {
      setRunStates((prev) => ({ ...prev, [prRef]: "error" }));
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/projects");
      if (!res.ok) throw new Error(await res.text());
      const projects: Project[] = await res.json();

      const involves = encodeURIComponent(login);
      const results = await Promise.allSettled(
        projects.map(async (p) => {
          const owner = encodeURIComponent(p.owner);
          const name = encodeURIComponent(p.name);
          const r = await fetch(
            `/api/repos/${owner}/${name}/pulls?state=open&base=main&involves=${involves}`,
          );
          if (r.status === 404) return [] as TaggedPR[];
          if (!r.ok) throw new Error(await r.text());
          const data: { repo: string; pulls: PullRequest[] } = await r.json();
          return data.pulls.map((pr) => ({ ...pr, repo: data.repo }));
        }),
      );

      const aggregated: TaggedPR[] = results.flatMap((result) =>
        result.status === "fulfilled" ? result.value : [],
      );

      aggregated.sort((a, b) => b.Number - a.Number);

      setPulls(aggregated);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  }, [login]);

  useEffect(() => {
    if (!login) {
      setPulls([]);
      setHasLoaded(false);
      return;
    }
    void load();
  }, [load, login]);

  const authLoading = status === null;
  const unauthenticated = status !== null && !login;
  // Treat as loading while auth is in-flight, while PRs are fetching, or
  // when we have a login but haven't completed the first fetch yet (prevents
  // a one-frame flash of the empty state between auth resolving and the effect
  // firing to start the PR load).
  const isLoading = authLoading || loading || (!!login && !hasLoaded);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pull Requests</CardTitle>
        <CardDescription>
          Open PRs to main that you authored, were asked to review, or have reviewed.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        )}

        {!isLoading && unauthenticated && (
          <p className="text-sm text-muted-foreground">Sign in to see your pull requests.</p>
        )}

        {!isLoading && error && <p className="text-sm text-destructive">{error}</p>}

        {!isLoading && !unauthenticated && !error && pulls.length === 0 && (
          <p className="text-sm text-muted-foreground">No open pull requests involving you.</p>
        )}

        {!isLoading && !error && pulls.length > 0 && (
          <Table>
            <TableBody>
              {pulls.map((pr) => (
                <TableRow
                  key={`${pr.repo}#${pr.Number}`}
                  className="cursor-pointer"
                  onClick={() => setSelectedPR(pr)}
                >
                  <TableCell className="align-middle w-0 whitespace-nowrap pr-4">
                    <a
                      href={`https://github.com/${pr.repo}/pull/${pr.Number}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-sm text-primary underline-offset-4 hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      #{pr.Number}
                    </a>
                  </TableCell>
                  <TableCell className="py-2">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2 text-sm">
                        <Badge variant={prStateVariant(pr.State)}>
                          {pr.State}
                        </Badge>
                        <span className="font-medium text-zinc-600 dark:text-zinc-400">{pr.AuthorLogin}</span>
                        <span>{pr.Title}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground font-mono">
                        <span>{pr.repo}</span>
                        <span>{pr.HeadBranch} → {pr.BaseBranch}</span>
                        <span className="ml-auto">{formatRelativeTime(pr.UpdatedAt)}</span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell
                    className="align-middle w-0 whitespace-nowrap pl-4"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ActionButton
                      prRef={`${pr.repo}#${pr.Number}`}
                      isMine={pr.AuthorLogin.toLowerCase() === login.toLowerCase()}
                      state={runStates[`${pr.repo}#${pr.Number}`] ?? "idle"}
                      onStart={startRun}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <PRRunHistoryModal
        prRef={selectedPR ? `${selectedPR.repo}#${selectedPR.Number}` : ""}
        prTitle={selectedPR?.Title ?? ""}
        open={selectedPR !== null}
        onOpenChange={(open) => { if (!open) setSelectedPR(null); }}
      />
    </Card>
  );
}
